// Which CRM this device's jobs came from (docs/SITES.md). The live site and
// the test site are different CRMs; when a site changes CRM (the cutover moved
// the live site from the dev CRM to the real one), jobs already on a device
// belong to the old CRM and must never be sent to the new one — their ids
// mean nothing there, and a change would land on the wrong job or none.
//
// So sync waits until this device's CRM is known to match the site's, and the
// screen offers to clear the device when it doesn't (CrmSwitchGate).
import { db, isChange, notifyChange } from './device-store';
import { createOnceKnown, type Cancel } from './once-known';

const DEVICE_CRM_KEY = 'm2q.deviceCrm';
/** Every device that held jobs before the CRM became a site setting got them from here. */
const BEFORE_SITES_CRM = 'https://hig-crm-dev.crm6.dynamics.com';

/** The CRM this site writes to, from /api/me; null until it has answered once. */
let siteCrm: string | null = null;

const siteCrmKnown = createOnceKnown<string>();

export function noteSiteCrm(crm: string | undefined): void {
  if (!crm) return;
  siteCrm = crm.replace(/\/+$/, '');
  siteCrmKnown.set(siteCrm);
}

/**
 * Run `work` once the site's CRM is known (now, if it is). Work that checks
 * deviceMatchesSite skips itself before then, so the app's first refresh
 * waits for /api/me instead of racing it. Returns the Cancel an effect cleans up with.
 */
export const whenSiteCrmKnown = (work: () => void): Cancel => siteCrmKnown.whenKnown(() => work());

function readDeviceCrm(): string | null {
  try {
    return localStorage.getItem(DEVICE_CRM_KEY);
  } catch {
    return null;
  }
}

function writeDeviceCrm(crm: string): void {
  try {
    localStorage.setItem(DEVICE_CRM_KEY, crm);
  } catch {
    // Storage blocked: the check runs again next time.
  }
}

async function deviceHasJobs(): Promise<boolean> {
  return (await (await db()).count('jobs')) > 0;
}

export type CrmCheck =
  | { state: 'unknown' }
  | { state: 'same' }
  /** The jobs here are from another CRM; `waiting` changes of theirs were never sent. */
  | { state: 'other'; waiting: number };

/** Does this device's data belong to the site's CRM? A device with no jobs takes the site's. */
export async function checkDeviceCrm(): Promise<CrmCheck> {
  if (!siteCrm) return { state: 'unknown' };
  let deviceCrm = readDeviceCrm();
  if (!deviceCrm) {
    deviceCrm = (await deviceHasJobs()) ? BEFORE_SITES_CRM : siteCrm;
    if (deviceCrm === siteCrm) writeDeviceCrm(siteCrm);
  }
  if (deviceCrm === siteCrm) return { state: 'same' };
  return { state: 'other', waiting: (await (await db()).getAll('outbox')).filter(isChange).length };
}

/** Sync and reference refreshes run only once this device is known to hold the site's CRM. */
export async function deviceMatchesSite(): Promise<boolean> {
  return (await checkDeviceCrm()).state === 'same';
}

/** Clear every job, photo, waiting change and reference list from another CRM, then take the site's. */
export async function clearForSiteCrm(): Promise<void> {
  if (!siteCrm) return;
  const tx = (await db()).transaction(['jobs', 'records', 'photos', 'outbox', 'reference', 'siteplans'], 'readwrite');
  await Promise.all([
    tx.objectStore('jobs').clear(),
    tx.objectStore('records').clear(),
    tx.objectStore('photos').clear(),
    tx.objectStore('outbox').clear(),
    tx.objectStore('reference').clear(),
    tx.objectStore('siteplans').clear(),
  ]);
  await tx.done;
  writeDeviceCrm(siteCrm);
  notifyChange();
}
